import { useEffect, useMemo, useRef, useState } from 'react'
import { ActionIcon, Button, Checkbox, Skeleton, Stack, TextInput, Tooltip } from '@mantine/core'
import {
  IconCalendarEvent, IconChevronLeft, IconChevronRight, IconMailOpened, IconRefresh,
  IconSearch, IconUserPlus,
} from '@tabler/icons-react'
import type { UseQueryResult } from '@tanstack/react-query'
import { chamadosApi, type EntradaItem, type EntradaResponse, type InboxCounts } from '@/api/chamados'
import { formatApiError } from '@/api/errors'
import { WidgetCard } from '@/components/ui/WidgetCard'
import { CardHeader } from '@/components/ui/CardHeader'
import { EstadoDeConsulta } from '@/components/ui/EstadoDeConsulta'
import { LinhaDoChamado } from './LinhaDoChamado'
import { CabecalhoDasColunas, SeletorDeOrdem } from './CabecalhoDasColunas'
import { proximaOrdem, type Ordem } from './ordem'
import { diaEMes } from './pastas'
import classes from './Entrada.module.css'

interface ListaDaPastaProps {
  titulo: string
  consulta: UseQueryResult<EntradaResponse>
  aviso: InboxCounts['aviso_feriado_local']
  busca: string
  onBusca: (q: string) => void
  pagina: { offset: number; limit: number; onMudar: (offset: number) => void }
  viewerUserId: number | null | undefined
  podeAtribuir: boolean
  onAtualizar: () => void
  onDepoisDoLote: () => void
  onAbrir: (id: number) => void
  /** A ordem escolhida (`?ordem=&dir=`); `null` é a padrão (spec 2026-10-21). */
  ordem: Ordem | null
  onOrdem: (ordem: Ordem | null) => void
  /** A pasta tem a coluna Responsável (spec 2026-10-22 §2). */
  comResponsavel?: boolean
  /** O `state` do link do título: a origem que dá ↑ ↓ ao detalhe (spec 2026-10-21 §4). */
  estadoAoAbrir?: unknown
  /** No computador a lista rola dentro do cartão; a barra de cima fica parada. */
  rolagemPropria?: boolean
}

type Falha = { id: number; motivo: string }

function motivoDaFalha(err: unknown): string {
  const status = (err as { response?: { status?: number } })?.response?.status
  if (status === 404) return 'você não tem acesso a este chamado'
  if (status === 403) return 'você não pode fazer isso neste chamado'
  return formatApiError(err, 'a ação falhou')
}

function resumo(ok: number, singular: string, plural: string, falhas: Falha[]): string {
  const feitos = `${ok} ${ok === 1 ? singular : plural}`
  if (!falhas.length) return `${feitos}.`
  const quais = falhas.map((f) => `#${f.id} (${f.motivo})`).join('; ')
  return `${feitos}, ${falhas.length} não ${falhas.length === 1 ? 'pôde' : 'puderam'}: ${quais}`
}

/** Negrito no topo (§5.6). O servidor já ordena; a ordenação estável aqui só garante que uma
 *  página nunca mostre linha comum acima de linha em negrito. Só na ordem padrão: com uma coluna
 *  escolhida ela desfaria a escolha (spec 2026-10-21 §2.2). */
function negritoPrimeiro(itens: EntradaItem[]): EntradaItem[] {
  return [...itens].sort((a, b) => Number(b.negrito) - Number(a.negrito))
}

/**
 * Cartão da pasta aberta (spec §3, §7): cabeçalho com a busca, barra de ações em lote e a lista.
 * Não usa o `ChamadoToolbar` de maio: ele traz Arquivar e Marcar lido (que esta Entrada não tem)
 * e paginação numerada, e não tem Atualizar, Atribuir, Marcar como não lido nem "1–N de T".
 */
export function ListaDaPasta({
  titulo, consulta, aviso, busca, onBusca, pagina, viewerUserId, podeAtribuir,
  onAtualizar, onDepoisDoLote, onAbrir, estadoAoAbrir, ordem, onOrdem, comResponsavel = false,
  rolagemPropria = false,
}: ListaDaPastaProps) {
  // Trocar de pasta ou de página volta a lista ao topo: com rolagem própria, o elemento que
  // rola é o mesmo e guardaria a posição da pasta anterior.
  const rolagem = useRef<HTMLDivElement | null>(null)
  useEffect(() => { if (rolagem.current) rolagem.current.scrollTop = 0 }, [titulo, pagina.offset, ordem])
  const itens = useMemo(
    () => (ordem ? consulta.data?.items ?? [] : negritoPrimeiro(consulta.data?.items ?? [])),
    [consulta.data, ordem],
  )
  const total = consulta.data?.total ?? 0
  const [selecionados, setSelecionados] = useState<Set<number>>(new Set())
  const [resultado, setResultado] = useState<string | null>(null)
  const [emLote, setEmLote] = useState(false)

  // Trocar de pasta, de busca ou de página zera a seleção: o lote é sobre o que está na tela.
  useEffect(() => { setSelecionados(new Set()) }, [consulta.data])

  const todos = itens.length > 0 && selecionados.size === itens.length
  const algum = selecionados.size > 0 && !todos
  const escolhidos = itens.filter((i) => selecionados.has(i.id))

  async function emCadaUm(
    acao: (i: EntradaItem) => Promise<string | null>,
    singular: string, plural: string,
  ) {
    setEmLote(true)
    setResultado(null)
    const falhas: Falha[] = []
    let ok = 0
    // Um por vez, pela MESMA rota da ação unitária (§7): cada item passa pela regra e grava
    // a própria trilha. Sem endpoint de lote.
    for (const item of escolhidos) {
      try {
        const recusa = await acao(item)
        if (recusa) falhas.push({ id: item.id, motivo: recusa })
        else ok += 1
      } catch (err) {
        falhas.push({ id: item.id, motivo: motivoDaFalha(err) })
      }
    }
    setResultado(resumo(ok, singular, plural, falhas))
    setEmLote(false)
    onDepoisDoLote()
  }

  const marcarNaoLido = () => emCadaUm(async (i) => {
    const r = await chamadosApi.marcarNaoLido(i.id)
    return r.data?.status === 'sem_movimento' ? 'não há movimento para desfazer' : null
  }, 'marcado como não lido', 'marcados como não lidos')

  const atribuirAMim = () => emCadaUm(async (i) => {
    // GMUD tem fluxo próprio de atribuição (§7): entra na seleção só para "não lido".
    if (i.request_type === 'gmud') return 'a GMUD segue o fluxo da própria GMUD'
    if (viewerUserId == null) return 'sessão sem usuário'
    // A mesma rota da atribuição unitária (§7).
    await chamadosApi.assign(i.tenant_id, i.id, viewerUserId)
    return null
  }, 'atribuído', 'atribuídos')

  const de = total === 0 ? 0 : pagina.offset + 1
  const ate = pagina.offset + itens.length

  return (
    <WidgetCard
      p={0}
      className={classes.cartao}
      data-com-responsavel={comResponsavel}
      style={rolagemPropria ? { height: '100%', display: 'flex', flexDirection: 'column' } : undefined}
    >
      <CardHeader
        title={titulo}
        extra={
          <TextInput
            className={classes.busca}
            size="xs"
            placeholder="Buscar chamado"
            aria-label="Buscar chamado por número, título, solicitante, ambiente ou organização"
            leftSection={<IconSearch size={14} aria-hidden />}
            value={busca}
            onChange={(e) => onBusca(e.currentTarget.value)}
          />
        }
      />

      {aviso && (
        <div className={classes.aviso} role="note">
          <IconCalendarEvent size={16} aria-hidden />
          {`Hoje (${diaEMes(aviso.data)}) é feriado em ${aviso.local}.`}
        </div>
      )}

      <div className={classes.barra}>
        <div className={classes.barraLote}>
          <Checkbox
            size="xs"
            aria-label="Selecionar todos"
            checked={todos}
            indeterminate={algum}
            disabled={itens.length === 0}
            onChange={() => setSelecionados(todos ? new Set() : new Set(itens.map((i) => i.id)))}
          />
        </div>
        <Tooltip label="Atualizar">
          <ActionIcon variant="subtle" aria-label="Atualizar" onClick={onAtualizar} loading={consulta.isFetching}>
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
        <div className={classes.barraLote}>
          {podeAtribuir && (
            <Button
              size="xs" variant="subtle" leftSection={<IconUserPlus size={14} />}
              disabled={!selecionados.size || emLote} onClick={atribuirAMim}
            >
              Atribuir a mim
            </Button>
          )}
          <Button
            size="xs" variant="subtle" leftSection={<IconMailOpened size={14} />}
            disabled={!selecionados.size || emLote} onClick={marcarNaoLido}
          >
            Marcar como não lido
          </Button>
        </div>
        <SeletorDeOrdem ordem={ordem} onEscolher={onOrdem} comResponsavel={comResponsavel} />
        <span className={classes.intervalo}>
          {`${de}–${ate} de ${total}`}
          {total > pagina.limit && (
            <>
              <ActionIcon
                size="sm" variant="subtle" aria-label="Página anterior"
                disabled={pagina.offset === 0}
                onClick={() => pagina.onMudar(Math.max(0, pagina.offset - pagina.limit))}
              >
                <IconChevronLeft size={14} />
              </ActionIcon>
              <ActionIcon
                size="sm" variant="subtle" aria-label="Próxima página"
                disabled={ate >= total}
                onClick={() => pagina.onMudar(pagina.offset + pagina.limit)}
              >
                <IconChevronRight size={14} />
              </ActionIcon>
            </>
          )}
        </span>
      </div>

      {resultado && <div className={classes.resultado} role="status">{resultado}</div>}

      {/* Parado como a barra; o gutter igual ao da lista mantém cada rótulo sobre a coluna
          quando a lista ganha barra de rolagem (spec 2026-10-21 §2.2). */}
      <div style={rolagemPropria ? { overflowY: 'hidden', scrollbarGutter: 'stable' } : undefined}>
        <CabecalhoDasColunas
          ordem={ordem}
          onOrdenar={(c) => onOrdem(proximaOrdem(ordem, c))}
          comResponsavel={comResponsavel}
        />
      </div>

      {/* O alerta de erro ganha o respiro do cartão; a lista vai de borda a borda. */}
      <div
        ref={rolagem}
        data-testid="entrada-lista-rolagem"
        className={consulta.isError && consulta.data === undefined ? classes.comRespiro : undefined}
        style={rolagemPropria ? { flex: 1, minHeight: 0, overflowY: 'auto', scrollbarGutter: 'stable' } : undefined}
      >
        <EstadoDeConsulta
          consulta={consulta}
          vazio={itens.length === 0}
          carregando={
            <Stack gap={8} p={16}>
              <Skeleton height={36} radius="sm" />
              <Skeleton height={36} radius="sm" />
              <Skeleton height={36} radius="sm" />
            </Stack>
          }
          estadoVazio={
            <div className={classes.vazio}>
              {busca.trim() ? 'Nenhum chamado desta pasta corresponde à busca.' : 'Nenhum chamado nesta pasta.'}
            </div>
          }
        >
          <ul className={classes.lista}>
            {itens.map((item) => (
              <LinhaDoChamado
                key={item.id}
                item={item}
                viewerUserId={viewerUserId}
                mostrarFila={(consulta.data?.minhas_filas_total ?? 0) >= 2}
                mostrarResponsavel={comResponsavel}
                selecionado={selecionados.has(item.id)}
                onSelecionar={(marcado) => setSelecionados((prev) => {
                  const next = new Set(prev)
                  if (marcado) next.add(item.id)
                  else next.delete(item.id)
                  return next
                })}
                onAbrir={() => onAbrir(item.id)}
                estadoAoAbrir={estadoAoAbrir}
              />
            ))}
          </ul>
        </EstadoDeConsulta>
      </div>
    </WidgetCard>
  )
}
